import type { PublicAnnouncement } from 'types';
import { AlertTriangle, Info, Megaphone, X } from 'lucide-react';
import { isSafeCtaUrl, isInternalCtaUrl } from 'helpers/announcement-url';
import { cn } from 'helpers/cn';
import { NavLink } from './NavLink';

type AnnouncementBarProps = {
  announcement: PublicAnnouncement;
  onDismiss?: () => void;
};

/**
 * Console tokens only, one wash per severity — raw rose/blue palette ramps with
 * hand-held `dark:` pairs predate the token set and made this the one surface
 * that ignored the theme. The bar sits in the desk's stack of floating panels,
 * so it draws like one: rounded-panel and a hairline, not a flush strip.
 */
const SEVERITY_STYLES = {
  // Filled, glowing and pulsing: urgent is the one severity a user cannot dismiss,
  // and it exists for the cases where being scrolled past is the failure. The solid
  // pair is identical in both themes, and the glow lifts it off either desk.
  urgent: {
    icon: AlertTriangle,
    container: 'bg-danger-solid text-danger-solid-fg border-transparent shadow-[0_2px_24px_rgba(190,18,60,0.45)]',
    iconChip: 'border-danger-solid-fg/40 text-danger-solid-fg animate-pulse',
    title: 'font-semibold',
    message: 'text-danger-solid-fg/85',
    cta: 'bg-danger-solid-fg text-danger-solid hover:opacity-90',
    dismiss: 'text-danger-solid-fg/80 hover:bg-danger-solid-fg/15 hover:text-danger-solid-fg',
  },
  // Filled accent, not a wash: the 7% accent-bg tint measured ~1.1:1 against the
  // panel, which reads as tone-of-voice rather than a notice. --cta is the same
  // blue in both themes, so the bar stands out equally on either desk.
  info: {
    icon: Info,
    container: 'bg-cta text-cta-foreground border-transparent',
    iconChip: 'border-cta-foreground/30 text-cta-foreground',
    title: 'font-medium',
    message: 'text-cta-foreground/85',
    cta: 'bg-cta-foreground text-cta hover:opacity-90',
    dismiss: 'text-cta-foreground/80 hover:bg-cta-foreground/15 hover:text-cta-foreground',
  },
  neutral: {
    icon: Megaphone,
    container: 'bg-panel border-line text-foreground',
    iconChip: 'border-line text-dim',
    title: 'font-medium',
    message: 'text-dim',
    cta: 'border-input text-foreground hover:bg-hover border bg-transparent',
    dismiss: 'text-dim hover:bg-hover hover:text-foreground',
  },
} as const;

/**
 * Presentational only. It renders whatever it is handed, which is what lets the
 * Super Admin screen preview unsaved form values through the same component
 * the product renders.
 */
export const AnnouncementBar = ({ announcement, onDismiss }: AnnouncementBarProps) => {
  const { severity, title, message, ctaLabel, ctaUrl } = announcement;
  const styles = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.neutral;
  const Icon = styles.icon;

  // Urgent is the one severity that cannot be dismissed: it exists for the
  // cases where being closed and forgotten is the failure.
  const canDismiss = !!onDismiss && severity !== 'urgent';

  // Never trust a stored href to be renderable. Validation happens on write,
  // but a row written before a validation change would still be served.
  const showCta = !!ctaLabel && !!ctaUrl && isSafeCtaUrl(ctaUrl);

  const ctaClassName = cn(
    'flex h-7 shrink-0 items-center whitespace-nowrap rounded-lg px-3 text-meta font-medium',
    'transition-colors duration-150 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
    styles.cta,
  );

  return (
    <div
      className={cn('rounded-panel flex items-center gap-3 border px-3.5 py-2.5', styles.container)}
      // Urgent interrupts a screen reader; the quieter severities wait their turn.
      role={severity === 'urgent' ? 'alert' : 'status'}
    >
      {/* Same icon treatment as AlertBanner, which renders directly below this
          bar in the desk — the two notices read as siblings, not two designs. */}
      <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg border', styles.iconChip)}>
        <Icon className="size-4" strokeWidth={1.5} />
      </span>

      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className={cn('text-body', styles.title)}>{title}</span>
        {!!message && <span className={cn('text-body', styles.message)}>{message}</span>}
      </div>

      {showCta &&
        (isInternalCtaUrl(ctaUrl!) ? (
          <NavLink to={ctaUrl!} className={ctaClassName}>
            {ctaLabel}
          </NavLink>
        ) : (
          <a href={ctaUrl!} target="_blank" rel="noopener noreferrer" className={ctaClassName}>
            {ctaLabel}
          </a>
        ))}

      {canDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          // size-9 on a phone, 28px beside a cursor: the console's touch floor.
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 md:size-7',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            styles.dismiss,
          )}
          aria-label="Dismiss announcement"
        >
          <X className="size-4" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
};
